import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAdminOverview, type AdminOverviewResponse } from "../../lib/admin-overview";
import { downloadAdminCatalogStockExport } from "../../lib/admin-catalog";
import { formatCurrency, formatDateTime } from "../../lib/utils";

function statusTone(status: string) {
  switch (status) {
    case "Paid":
      return "border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-900/70 dark:bg-cyan-950/40 dark:text-cyan-300";
    case "Pending":
      return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-300";
    case "Processing":
      return "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/70 dark:bg-violet-950/40 dark:text-violet-300";
    case "Delivered":
      return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300";
    default:
      return "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300";
  }
}

function roleTone(role: AdminOverviewResponse["recentUsers"][number]["role"]) {
  if (role === "ADMIN") {
    return "text-red-600 dark:text-red-300";
  }
  if (role === "SALES") {
    return "text-blue-600 dark:text-blue-300";
  }

  return "text-emerald-600 dark:text-emerald-300";
}

function buildRevenuePath(values: number[], width: number, height: number) {
  if (values.length === 0) {
    return "";
  }

  const max = Math.max(...values, 1);
  return values
    .map((value, index) => {
      const x = values.length === 1 ? width / 2 : (index / (values.length - 1)) * width;
      const y = height - (value / max) * height;
      return `${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");
}

function initialsForName(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

const emptyOverview: AdminOverviewResponse = {
  metrics: {
    totalOrders: 0,
    paidOrders: 0,
    pendingOrders: 0,
    processingOrders: 0,
    deliveredOrders: 0,
    paidRevenue: 0,
    totalUsers: 0,
    activeProducts: 0,
    lowStockProducts: 0,
    stockUnits: 0
  },
  revenueSeries: [],
  statusBreakdown: [],
  recentOrders: [],
  stockWatch: [],
  recentUsers: []
};

export function AdminDashboardPage() {
  const [data, setData] = useState<AdminOverviewResponse>(emptyOverview);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeRevenueIndex, setActiveRevenueIndex] = useState<number>(-1);
  const [activeStatus, setActiveStatus] = useState<string | null>(null);
  const [exportingStock, setExportingStock] = useState(false);

  async function handleExportStock() {
    setExportingStock(true);
    try {
      await downloadAdminCatalogStockExport();
    } catch (exportErr) {
      alert(exportErr instanceof Error ? exportErr.message : "Failed to export stock to Excel.");
    } finally {
      setExportingStock(false);
    }
  }


  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const result = await fetchAdminOverview();
        if (!ignore) {
          setData(result);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load dashboard overview.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      ignore = true;
    };
  }, []);

  const headlineCards = useMemo(
    () => [
      {
        label: "Paid revenue",
        value: formatCurrency(data.metrics.paidRevenue),
        detail: `${data.metrics.paidOrders} paid orders`,
        tone: "from-cyan-500/18 via-white/40 to-transparent"
      },
      {
        label: "Pending orders",
        value: String(data.metrics.pendingOrders),
        detail: `${data.metrics.processingOrders} processing now`,
        tone: "from-amber-500/16 via-white/40 to-transparent"
      },
      {
        label: "Active catalog",
        value: String(data.metrics.activeProducts),
        detail: `${data.metrics.lowStockProducts} need restock attention`,
        tone: "from-violet-500/16 via-white/40 to-transparent"
      },
      {
        label: "Customers and staff",
        value: String(data.metrics.totalUsers),
        detail: `${data.metrics.stockUnits} units currently on hand`,
        tone: "from-emerald-500/16 via-white/40 to-transparent"
      }
    ],
    [data.metrics]
  );

  const revenueValues = data.revenueSeries.map((item) => item.paidRevenue);
  const revenuePath = useMemo(() => buildRevenuePath(revenueValues, 560, 190), [revenueValues]);
  const revenuePeak = revenueValues.length > 0 ? Math.max(...revenueValues, 0) : 0;
  const resolvedRevenueIndex =
    activeRevenueIndex >= 0 && activeRevenueIndex < data.revenueSeries.length ? activeRevenueIndex : data.revenueSeries.length - 1;
  const activeRevenuePoint = resolvedRevenueIndex >= 0 ? data.revenueSeries[resolvedRevenueIndex] : null;
  const activeStatusItem =
    data.statusBreakdown.find((item) => item.status === activeStatus) || data.statusBreakdown[0] || null;

  return (
    <div className="space-y-6 pb-6">
      <div className="relative overflow-hidden rounded-[36px] border border-white/10 bg-slate-950 px-6 py-7 text-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.18),transparent_32%),radial-gradient(circle_at_80%_20%,rgba(59,130,246,0.18),transparent_28%),linear-gradient(135deg,rgba(15,23,42,0.9),rgba(2,6,23,0.96))]" />
        <div className="relative flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Admin overview</p>
            <h1 className="mt-3 max-w-4xl text-4xl font-semibold tracking-tight">System overview</h1>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-300">Sales, fulfillment, stock, and recent activity.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Link className="rounded-full border border-white/15 bg-white/8 px-4 py-2 text-sm font-semibold transition hover:bg-white/14" to="/admin/orders">
              Open orders
            </Link>
            <Link className="rounded-full border border-white/15 bg-white/8 px-4 py-2 text-sm font-semibold transition hover:bg-white/14" to="/admin/catalog">
              Review catalog
            </Link>
            <Link className="rounded-full border border-white/15 bg-white/8 px-4 py-2 text-sm font-semibold transition hover:bg-white/14" to="/admin/users">
              Check users
            </Link>
          </div>
        </div>
      </div>

      {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {headlineCards.map((card) => (
          <div className="admin-card relative overflow-hidden rounded-[30px] p-5" key={card.label}>
            <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${card.tone}`} />
            <div className="relative">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-nv-slate">{card.label}</p>
              <p className="mt-4 break-words text-3xl font-semibold tracking-tight text-nv-ink">{card.value}</p>
              <p className="mt-3 text-sm text-nv-slate">{card.detail}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="items-start grid gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(340px,0.95fr)]">
        <div className="admin-card min-w-0 rounded-[32px] p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Revenue trend</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Paid revenue over the last six months</h2>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                {activeRevenuePoint ? `Selected month: ${activeRevenuePoint.monthLabel}` : "Peak month"}
              </p>
              <p className="mt-2 text-xl font-semibold text-nv-ink">
                {activeRevenuePoint ? formatCurrency(activeRevenuePoint.paidRevenue) : formatCurrency(revenuePeak)}
              </p>
              <p className="mt-1 text-xs text-nv-slate">
                {activeRevenuePoint ? `${activeRevenuePoint.paidOrders} paid orders` : "Hover a point for detail"}
              </p>
            </div>
          </div>

          <div className="mt-6 rounded-[28px] border border-black/6 bg-[linear-gradient(180deg,rgba(29,78,216,0.08),rgba(255,255,255,0))] p-4 dark:border-white/8">
            {loading ? (
              <div className="flex h-[240px] items-center justify-center text-sm text-nv-slate">Loading revenue chart...</div>
            ) : data.revenueSeries.length > 0 ? (
              <div className="min-w-0">
                <svg className="h-[240px] w-full" viewBox="0 0 560 240" preserveAspectRatio="none" role="img">
                  <defs>
                    <linearGradient id="overviewRevenueStroke" x1="0%" x2="100%" y1="0%" y2="0%">
                      <stop offset="0%" stopColor="#06b6d4" />
                      <stop offset="100%" stopColor="#2563eb" />
                    </linearGradient>
                    <linearGradient id="overviewRevenueFill" x1="0%" x2="0%" y1="0%" y2="100%">
                      <stop offset="0%" stopColor="rgba(37,99,235,0.24)" />
                      <stop offset="100%" stopColor="rgba(37,99,235,0.02)" />
                    </linearGradient>
                  </defs>
                  {[0, 1, 2, 3].map((line) => (
                    <line
                      key={line}
                      x1="0"
                      x2="560"
                      y1={20 + line * 55}
                      y2={20 + line * 55}
                      stroke="rgba(90,103,120,0.18)"
                      strokeDasharray="4 8"
                    />
                  ))}
                  <path d={`${revenuePath} L 560 190 L 0 190 Z`} fill="url(#overviewRevenueFill)" transform="translate(0 20)" />
                  <path d={revenuePath} fill="none" stroke="url(#overviewRevenueStroke)" strokeLinecap="round" strokeWidth="4" transform="translate(0 20)" />
                  {data.revenueSeries.map((point, index) => {
                    const max = Math.max(...revenueValues, 1);
                    const x = data.revenueSeries.length === 1 ? 280 : (index / (data.revenueSeries.length - 1)) * 560;
                    const y = 20 + (190 - (point.paidRevenue / max) * 190);
                    const active = index === resolvedRevenueIndex;
                    return (
                      <g key={point.monthKey}>
                        <circle
                          cx={x}
                          cy={y}
                          fill="transparent"
                          onFocus={() => setActiveRevenueIndex(index)}
                          onMouseEnter={() => setActiveRevenueIndex(index)}
                          r="16"
                          tabIndex={0}
                        />
                        <circle
                          cx={x}
                          cy={y}
                          fill="#ffffff"
                          r={active ? "7" : "5"}
                          stroke={active ? "#06b6d4" : "#0f172a"}
                          strokeWidth={active ? "4" : "3"}
                        />
                      </g>
                    );
                  })}
                </svg>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-nv-slate sm:grid-cols-3 xl:grid-cols-6">
                  {data.revenueSeries.map((point, index) => (
                    <button
                      className={`min-w-0 rounded-2xl px-2 py-2 text-left transition ${
                        index === resolvedRevenueIndex ? "bg-black/[0.06] text-nv-ink dark:bg-white/[0.08]" : ""
                      }`}
                      key={point.monthKey}
                      onClick={() => setActiveRevenueIndex(index)}
                      onMouseEnter={() => setActiveRevenueIndex(index)}
                      type="button"
                    >
                      <p>{point.monthLabel}</p>
                      <p className="mt-1 break-words normal-case tracking-normal text-[11px] text-nv-ink">{formatCurrency(point.paidRevenue)}</p>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex h-[240px] items-center justify-center text-sm text-nv-slate">No paid revenue history yet.</div>
            )}
          </div>
        </div>

        <div className="min-w-0 space-y-4">
          <div className="admin-card rounded-[32px] p-6">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Order mix</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Fulfillment status breakdown</h2>
            {activeStatusItem ? (
              <div className="mt-4 rounded-2xl border border-black/6 bg-black/[0.03] px-4 py-3 text-sm dark:border-white/8 dark:bg-white/[0.04]">
                <p className="font-semibold text-nv-ink">{activeStatusItem.status}</p>
                <p className="mt-1 text-nv-slate">
                  {activeStatusItem.total} orders,{" "}
                  {data.metrics.totalOrders > 0 ? `${Math.round((activeStatusItem.total / data.metrics.totalOrders) * 100)}% of all orders` : "0% of all orders"}
                </p>
              </div>
            ) : null}
            <div className="mt-6 space-y-4">
              {(loading ? [] : data.statusBreakdown).map((item) => {
                const totalBase = Math.max(data.metrics.totalOrders, 1);
                const width = `${Math.max((item.total / totalBase) * 100, 8)}%`;
                const active = activeStatusItem?.status === item.status;
                return (
                  <button
                    className={`block w-full rounded-2xl p-2 text-left transition ${active ? "bg-black/[0.04] dark:bg-white/[0.04]" : ""}`}
                    key={item.status}
                    onClick={() => setActiveStatus(item.status)}
                    onMouseEnter={() => setActiveStatus(item.status)}
                    onMouseLeave={() => setActiveStatus(null)}
                    type="button"
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(item.status)}`}>{item.status}</span>
                      <span className="text-sm font-semibold text-nv-ink">{item.total}</span>
                    </div>
                    <div className="h-3 rounded-full bg-black/[0.06] dark:bg-white/[0.08]">
                      <div
                        className={`h-full rounded-full bg-[linear-gradient(90deg,#06b6d4,#2563eb)] transition-all ${active ? "shadow-[0_0_0_3px_rgba(6,182,212,0.12)]" : ""}`}
                        style={{ width }}
                      />
                    </div>
                  </button>
                );
              })}
              {loading ? <div className="text-sm text-nv-slate">Loading order mix...</div> : null}
            </div>
          </div>

          <div className="admin-card rounded-[32px] p-6">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">System pulse</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="field-soft p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-nv-slate">Order conversion</p>
                <p className="mt-2 text-2xl font-semibold text-nv-ink">
                  {data.metrics.totalOrders > 0 ? `${Math.round((data.metrics.paidOrders / data.metrics.totalOrders) * 100)}%` : "0%"}
                </p>
                <p className="mt-2 text-sm text-nv-slate">Share of all orders already fully paid.</p>
              </div>
              <div className="field-soft p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-nv-slate">Low stock pressure</p>
                <p className="mt-2 text-2xl font-semibold text-nv-ink">{data.metrics.lowStockProducts}</p>
                <p className="mt-2 text-sm text-nv-slate">Active products at or below five units.</p>
              </div>
              <div className="field-soft p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-nv-slate">Delivered orders</p>
                <p className="mt-2 text-2xl font-semibold text-nv-ink">{data.metrics.deliveredOrders}</p>
                <p className="mt-2 text-sm text-nv-slate">Orders already closed out operationally.</p>
              </div>
              <div className="field-soft p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-nv-slate">Inventory volume</p>
                <p className="mt-2 text-2xl font-semibold text-nv-ink">{data.metrics.stockUnits}</p>
                <p className="mt-2 text-sm text-nv-slate">Total units across active catalog items.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="items-start grid gap-4 xl:grid-cols-3">
        <div className="admin-card min-w-0 rounded-[32px] p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Recent orders</p>
              <h2 className="mt-2 text-xl font-semibold tracking-tight text-nv-ink">Latest sales activity</h2>
            </div>
            <Link className="text-sm font-semibold text-nv-electric" to="/admin/orders">
              View all
            </Link>
          </div>
          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-black/10 text-left text-xs font-semibold uppercase tracking-[0.18em] text-nv-slate">
                  <th className="px-0 py-3">Order</th>
                  <th className="px-3 py-3">Customer</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.recentOrders.map((order) => (
                  <tr className="border-b border-black/5 last:border-b-0" key={order.id}>
                    <td className="px-0 py-4">
                      <p className="font-semibold text-nv-ink">#{order.id}</p>
                      <p className="mt-1 text-xs text-nv-slate">{formatDateTime(order.addedOn)}</p>
                    </td>
                    <td className="px-3 py-4">
                      <p className="font-semibold text-nv-ink">{order.customerName}</p>
                      <p className="mt-1 text-xs text-nv-slate">{order.phone || "No phone"}</p>
                    </td>
                    <td className="px-3 py-4">
                      <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(order.status)}`}>{order.status}</span>
                    </td>
                    <td className="px-3 py-4 text-right">
                      <p className="font-semibold text-nv-ink">{formatCurrency(order.total)}</p>
                      <p className="mt-1 text-xs text-nv-slate">Paid {formatCurrency(order.amountPaid)}</p>
                    </td>
                  </tr>
                ))}
                {!loading && data.recentOrders.length === 0 ? (
                  <tr>
                    <td className="px-0 py-6 text-sm text-nv-slate" colSpan={4}>
                      No orders available yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-card min-w-0 rounded-[32px] p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Stock watch</p>
              <h2 className="mt-2 text-xl font-semibold tracking-tight text-nv-ink">Low inventory exposure</h2>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 disabled:opacity-50 dark:text-emerald-300 dark:hover:text-emerald-200"
                disabled={exportingStock}
                onClick={() => void handleExportStock()}
                type="button"
              >
                {exportingStock ? "Exporting..." : "Export Excel"}
              </button>
              <Link className="text-sm font-semibold text-nv-electric" to="/admin/catalog">
                Open catalog
              </Link>
            </div>
          </div>
          <div className="mt-5 space-y-3">
            {data.stockWatch.map((item) => (
              <div className="field-soft flex items-center justify-between gap-4 p-4" key={item.id}>
                <div className="min-w-0">
                  <Link className="block truncate font-semibold text-nv-ink transition hover:text-nv-electric" to={`/shop/product/${item.slug}/${item.id}`}>
                    {item.name}
                  </Link>
                  <p className="mt-1 text-xs text-nv-slate">{item.category}</p>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-semibold ${item.quantity <= 5 ? "text-amber-600 dark:text-amber-300" : "text-nv-ink"}`}>{item.quantity}</p>
                  <p className="mt-1 text-xs text-nv-slate">{formatCurrency(item.price)}</p>
                </div>
              </div>
            ))}
            {!loading && data.stockWatch.length === 0 ? <div className="text-sm text-nv-slate">No active products found.</div> : null}
          </div>
        </div>

        <div className="admin-card min-w-0 rounded-[32px] p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Recent users</p>
              <h2 className="mt-2 text-xl font-semibold tracking-tight text-nv-ink">Newest people in the system</h2>
            </div>
            <Link className="text-sm font-semibold text-nv-electric" to="/admin/users">
              Manage users
            </Link>
          </div>
          <div className="mt-5 space-y-3">
            {data.recentUsers.map((user) => (
              <div className="field-soft flex items-center gap-4 p-4" key={user.id}>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-black/10 bg-black/[0.04] text-sm font-semibold text-nv-ink dark:border-white/10 dark:bg-white/[0.04]">
                  {initialsForName(user.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <p className="truncate font-semibold text-nv-ink">{user.name}</p>
                    <span className={`text-xs font-semibold uppercase tracking-[0.16em] ${roleTone(user.role)}`}>{user.role}</span>
                  </div>
                  <p className="mt-1 truncate text-xs text-nv-slate">{user.email || user.phone || "No contact recorded"}</p>
                  <p className="mt-1 text-xs text-nv-slate">{formatDateTime(user.registeredOn)}</p>
                </div>
              </div>
            ))}
            {!loading && data.recentUsers.length === 0 ? <div className="text-sm text-nv-slate">No users available yet.</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
